import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useToolStore, { TOOL_TYPES, hasToolPreview } from '../../store/useToolStore';
import Toolbar from '../Toolbar/Toolbar';
import { ToolControls, ToolPreview } from './ToolRouter';
import CodeEditor from '../CodeEditor/CodeEditor';
import ThreePanelLayout from '../Layout/ThreePanelLayout';
import TwoPanelLayout from '../Layout/TwoPanelLayout';
import SEO from '../SEO/SEO';
import { getSEOData } from '../../data/seoData';
import StoryboardFileList from './StoryboardConverter/StoryboardFileList';

const PATH_TO_TOOL_TYPE = {
  '/tools/loader': TOOL_TYPES.LOADER,
  '/tools/gradient': TOOL_TYPES.GRADIENT,
  '/tools/card': TOOL_TYPES.CARD,
  '/tools/animation': TOOL_TYPES.ANIMATION,
  '/tools/alert': TOOL_TYPES.ALERT,
  '/tools/photo-picker': TOOL_TYPES.PHOTO_PICKER,
  '/tools/json-to-model': TOOL_TYPES.JSON_TO_MODEL,
  '/tools/api-generator': TOOL_TYPES.API_GENERATOR,
  '/tools/storyboard-converter': TOOL_TYPES.STORYBOARD_CONVERTER,
  '/tools/postman-importer': TOOL_TYPES.POSTMAN_IMPORTER,
};

const PATH_TO_SEO_KEY = {
  '/tools/loader': 'loader',
  '/tools/gradient': 'gradient',
  '/tools/card': 'card',
  '/tools/animation': 'animation',
  '/tools/alert': 'alert',
  '/tools/photo-picker': 'photoPicker',
  '/tools/json-to-model': 'jsonToModel',
  '/tools/api-generator': 'apiGenerator',
  '/tools/storyboard-converter': 'storyboardConverter',
  '/tools/postman-importer': 'postmanImporter',
};

const ToolPage = ({ toolType }) => {
  const location = useLocation();
  const toolStoreType = PATH_TO_TOOL_TYPE[location.pathname] || TOOL_TYPES.LOADER;
  const setActiveTool = useToolStore((state) => state.setActiveTool);
  const generatedCode = useToolStore((state) => state.generatedCode);
  const generatedFiles = useToolStore((state) => state.generatedFiles);
  const selectedFileIndex = useToolStore((state) => state.selectedFileIndex);
  const previewInCodeWindow = useToolStore((state) => state.previewInCodeWindow);
  const previewFileIndex = useToolStore((state) => state.previewFileIndex);
  
  const [isToolbarVisible, setIsToolbarVisible] = useState(true);
  const lastScrollY = useRef(0);
  
  useEffect(() => {
    setActiveTool(toolStoreType);
  }, [toolStoreType, setActiveTool]);

  // Scroll-to-hide toolbar functionality
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollableContainers = document.querySelectorAll('.overflow-y-auto');
      
      let maxScroll = 0;
      scrollableContainers.forEach(container => {
        maxScroll = Math.max(maxScroll, container.scrollTop);
      });
      
      const totalScroll = currentScrollY + maxScroll;
      
      // Show toolbar when scrolling up or at top, hide when scrolling down
      if (totalScroll > lastScrollY.current && totalScroll > 50) {
        setIsToolbarVisible(false);
      } else {
        setIsToolbarVisible(true);
      }
      
      lastScrollY.current = totalScroll;
    };

    // Listen to scroll events on window and scrollable containers
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    const scrollableContainers = document.querySelectorAll('.overflow-y-auto');
    scrollableContainers.forEach(container => {
      container.addEventListener('scroll', handleScroll, { passive: true });
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      scrollableContainers.forEach(container => {
        container.removeEventListener('scroll', handleScroll);
      });
    };
  }, []);

  const seoKey = PATH_TO_SEO_KEY[location.pathname] || toolType || 'home';
  const seoData = getSEOData(seoKey);
  const hasPreview = hasToolPreview(toolStoreType);
  const isStoryboardConverter = toolStoreType === TOOL_TYPES.STORYBOARD_CONVERTER;
  const hasMultipleFiles = isStoryboardConverter && generatedFiles && generatedFiles.length > 0;

  // Get the code to display
  const codeToDisplay = hasMultipleFiles 
    ? (generatedFiles[selectedFileIndex]?.content || '') 
    : generatedCode;
  
  // Get the file name to display
  const fileNameToDisplay = hasMultipleFiles 
    ? generatedFiles[selectedFileIndex]?.name 
    : null;
  
  // Get preview code for code window (if dual preview is enabled)
  const previewCodeForWindow = hasMultipleFiles && previewInCodeWindow && previewFileIndex !== null
    ? generatedFiles[previewFileIndex]?.content
    : null;

  // Render code panel for storyboard converter with file list
  const renderStoryboardCodePanel = () => {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          {/* File List - Top on mobile, Left on desktop */}
          <div className="w-full lg:w-64 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 flex-shrink-0 h-48 lg:h-auto">
            <StoryboardFileList />
          </div>
          {/* Code Editor - Bottom on mobile, Right on desktop */}
          <div className="flex-1 min-h-0">
            <CodeEditor 
              code={codeToDisplay} 
              fileName={fileNameToDisplay}
              showPreview={previewInCodeWindow && previewFileIndex !== null}
              previewCode={previewCodeForWindow}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        image={seoData.image}
        jsonLd={seoData.jsonLd}
        type="website"
      />
      <div className="relative bg-white dark:bg-gray-900 transition-colors duration-200">
        {/* Toolbar Section - Fixed/Sticky with scroll-to-hide */}
        <div 
          className={`sticky top-0 z-20 bg-white dark:bg-gray-900 transition-transform duration-300 ${
            isToolbarVisible ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <Toolbar />
        </div>
        
        {/* Content Section - Boxed Layout with max-width 1400px, Sticky */}
        <div className="sticky top-[56px] z-10" style={{ height: 'calc(100vh - 56px)' }}>
          <div className="max-w-[1400px] mx-auto h-full">
            {hasPreview ? (
              <ThreePanelLayout
                leftPanel={<ToolControls />}
                middlePanel={<ToolPreview />}
                rightPanel={
                  isStoryboardConverter && hasMultipleFiles
                    ? renderStoryboardCodePanel()
                    : <CodeEditor 
                        code={codeToDisplay} 
                        fileName={fileNameToDisplay}
                        showPreview={previewInCodeWindow && previewFileIndex !== null}
                        previewCode={previewCodeForWindow}
                      />
                }
              />
            ) : (
              <TwoPanelLayout
                leftPanel={<ToolControls />}
                rightPanel={
                  isStoryboardConverter && hasMultipleFiles
                    ? renderStoryboardCodePanel()
                    : <CodeEditor 
                        code={codeToDisplay} 
                        fileName={fileNameToDisplay}
                        showPreview={previewInCodeWindow && previewFileIndex !== null}
                        previewCode={previewCodeForWindow}
                      />
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ToolPage;

